import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

const FADING_CLASS = "filter-bar-frame--fading"

// Drives the shared filter bar (see shared/_filter_bar.html.erb): opens option
// panels above the bar with a page overlay, applies selections to the URL, any
// nav links and the turbo frame named by data-filter-bar-frame-value. The
// dashboard's staggered card reveal runs when the frame contains dashboard
// columns and no-ops elsewhere.
export default class extends Controller {
  static targets = ["overlay", "box", "panel", "navLink", "calendar"]
  static values = {
    defaults: Object,
    frame: String
  }

  connect() {
    this.onFrameLoad = (event) => {
      // the frame itself on a filter change, or the frame it arrives inside on first load
      if (event.target.id === this.frameValue || event.target.querySelector(`#${this.frameValue}`)) this.reveal()
    }
    this.element.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.initCalendar()
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.onFrameLoad)
    if (this.calendar) this.calendar.destroy()
  }

  // Inline range calendar (same flatpickr the app's forms use)
  initCalendar() {
    if (!this.hasCalendarTarget) return

    const { dateFrom, dateTo } = this.calendarTarget.dataset
    this.calendar = flatpickr(this.calendarTarget, {
      inline: true,
      mode: "range",
      maxDate: "today",
      dateFormat: "Y-m-d",
      defaultDate: dateFrom && dateTo ? [dateFrom, dateTo] : null,
      onChange: (dates) => {
        if (dates.length === 2) this.applyPeriod(dates)
      }
    })
  }

  applyPeriod([from, to]) {
    const iso = (date) => flatpickr.formatDate(date, "Y-m-d")
    const label = `${flatpickr.formatDate(from, "j M y")} – ${flatpickr.formatDate(to, "j M y")}`
    this.close()

    const box = this.boxFor("period")
    box.querySelector(".filter-bar__box-label").textContent = label
    box.classList.add("filter-bar__box--active")

    const params = { date_from: iso(from), date_to: iso(to) }
    this.applyToUrls(params)
    this.reloadFrame(params)
  }

  // Quick links in the calendar panel (server-rendered dates)
  preset(event) {
    const { dateFrom, dateTo } = event.currentTarget.dataset
    const from = flatpickr.parseDate(dateFrom, "Y-m-d")
    const to = flatpickr.parseDate(dateTo, "Y-m-d")
    if (this.calendar) this.calendar.setDate([from, to], false)
    this.applyPeriod([from, to])
  }

  clearPeriod() {
    if (this.calendar) this.calendar.clear()
    this.close()

    const box = this.boxFor("period")
    box.querySelector(".filter-bar__box-label").textContent = box.dataset.defaultLabel
    box.classList.remove("filter-bar__box--active")

    const params = { date_from: "", date_to: "" }
    this.applyToUrls(params)
    this.reloadFrame(params)
  }

  get frame() {
    if (!this.frameValue) return null
    return this.element.querySelector(`turbo-frame#${this.frameValue}`)
  }

  toggle(event) {
    const param = event.currentTarget.dataset.param
    if (this.openParam === param) {
      this.close()
    } else {
      this.open(param)
    }
  }

  open(param) {
    this.close()
    this.openParam = param
    this.overlayTarget.hidden = false
    this.panelFor(param).hidden = false
    this.boxFor(param).classList.add("filter-bar__box--open")
  }

  close() {
    this.openParam = null
    if (this.hasOverlayTarget) this.overlayTarget.hidden = true
    this.panelTargets.forEach((panel) => panel.hidden = true)
    this.boxTargets.forEach((box) => box.classList.remove("filter-bar__box--open"))
  }

  // Options carry the URL param they set (data-param), which can differ from
  // their panel's box param when one box manages several params (the site box
  // holds both site_id and site_group_id options), plus the sibling params to
  // clear (data-reset-params).
  choose(event) {
    const { param, value, resetParams } = event.currentTarget.dataset
    const label = event.currentTarget.textContent.trim()
    const panel = event.currentTarget.closest(".filter-bar__panel")
    this.close()
    this.updateBox(panel.dataset.param, value, label)
    this.markSelected(panel, param, value)

    const params = { [param]: value }
    ;(resetParams || "").split(" ").forEach((reset) => {
      if (reset && reset !== param) params[reset] = ""
    })
    this.applyToUrls(params)
    this.reloadFrame(params)
  }

  // Nav clicks: let Turbo navigate, but fade the current frame out while the
  // next page loads.
  fadeOut() {
    this.frame?.classList.add(FADING_CLASS)
  }

  updateBox(param, value, label) {
    const box = this.boxFor(param)
    const isDefault = value === (this.defaultsValue[param] ?? "")
    box.querySelector(".filter-bar__box-label").textContent = isDefault ? box.dataset.defaultLabel : label
    box.classList.toggle("filter-bar__box--active", !isDefault)
  }

  markSelected(panel, param, value) {
    panel.querySelectorAll(".filter-bar__option").forEach((option) => {
      option.classList.toggle("filter-bar__option--selected",
        option.dataset.param === param && option.dataset.value === value)
    })
  }

  applyToUrls(params) {
    history.replaceState({}, "", this.withParams(window.location.href, params))
    this.navLinkTargets.forEach((link) => {
      link.setAttribute("href", this.withParams(link.getAttribute("href"), params))
    })
  }

  reloadFrame(params) {
    const frame = this.frame
    if (!frame) return

    // Frames rendered inline on first load carry their reload URL in data-src.
    // The old content stays in place (dimmed by the fading class) until Turbo
    // swaps in the response, so loading indicators have something to sit over
    // and the fetch can start immediately rather than waiting out the fade.
    const url = frame.getAttribute("src") || frame.dataset.src
    if (!url) return

    frame.classList.add(FADING_CLASS)
    this.loadFrame(frame, this.withParams(url, params))
  }

  loadFrame(frame, src) {
    frame.setAttribute("src", src)
    if (typeof frame.reload === "function") frame.reload()
  }

  // Fade the frame back in; on the dashboard, stagger the card reveal (first
  // card on the left, first on the right, second on the left...)
  reveal() {
    const frame = this.frame
    frame.classList.remove(FADING_CLASS)

    const columns = [...frame.querySelectorAll(".dashboard__column")].map(
      (column) => [...column.querySelectorAll(":scope > .dashboard-card")]
    )
    if (columns.length === 0) return

    const cards = []
    for (let row = 0; row < Math.max(...columns.map((c) => c.length), 0); row++) {
      columns.forEach((column) => { if (column[row]) cards.push(column[row]) })
    }

    cards.forEach((card, index) => {
      card.style.transitionDelay = `${index * 80}ms`
    })
    requestAnimationFrame(() => requestAnimationFrame(() => {
      cards.forEach((card) => card.classList.add("dashboard-card--revealed"))
    }))
  }

  withParams(url, params) {
    const parsed = new URL(url, window.location.origin)
    Object.entries(params).forEach(([param, value]) => {
      if (value === "" || value === (this.defaultsValue[param] ?? "")) {
        parsed.searchParams.delete(param)
      } else {
        parsed.searchParams.set(param, value)
      }
    })
    return parsed.pathname + parsed.search
  }

  boxFor(param) {
    return this.boxTargets.find((box) => box.dataset.param === param)
  }

  panelFor(param) {
    return this.panelTargets.find((panel) => panel.dataset.param === param)
  }
}
